import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    CheckCircleIcon,
    CircleNotchIcon,
    InfinityIcon,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const POLL_MS = 3000;
const MAX_POLLS = 40;

function formatUntil(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    } catch (_) {
        return "";
    }
}

export default function CheckoutSuccess() {
    const location = useLocation();
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const [state, setState] = useState({
        status: "checking",
        message: "Verifying your payment…",
        creditsAdded: 0,
        unlimitedDaysAdded: 0,
        balance: 0,
        isUnlimited: false,
        unlimitedUntil: null,
    });

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sessionId = params.get("session_id");
        if (!sessionId) {
            setState((s) => ({
                ...s,
                status: "error",
                message: "Missing checkout session id.",
            }));
            return;
        }

        let cancelled = false;
        (async () => {
            // First attempt: a single force-reconcile call. This is the
            // source-of-truth path — it queries Stripe directly and grants
            // credits to the user_id stored on the txn (no auth needed).
            try {
                const { data } = await api.post(
                    `/checkout/reconcile/${sessionId}`,
                );
                if (!cancelled && data.payment_status === "paid") {
                    await refresh();
                    setState({
                        status: "paid",
                        message: "Payment successful.",
                        creditsAdded: data.credits_added,
                        unlimitedDaysAdded: data.unlimited_days_added,
                        balance: data.new_balance,
                        isUnlimited: data.is_unlimited,
                        unlimitedUntil: data.unlimited_until,
                    });
                    return;
                }
            } catch (_) {
                /* fall through to status polling */
            }

            for (let i = 0; i < MAX_POLLS; i++) {
                try {
                    const { data } = await api.post(
                        `/checkout/reconcile/${sessionId}`,
                    );
                    if (cancelled) return;
                    if (data.payment_status === "paid") {
                        await refresh();
                        setState({
                            status: "paid",
                            message: "Payment successful.",
                            creditsAdded: data.credits_added,
                            unlimitedDaysAdded: data.unlimited_days_added,
                            balance: data.new_balance,
                            isUnlimited: data.is_unlimited,
                            unlimitedUntil: data.unlimited_until,
                        });
                        return;
                    }
                    if (data.status === "expired") {
                        setState((s) => ({
                            ...s,
                            status: "expired",
                            message: "Checkout expired.",
                            balance: data.new_balance,
                        }));
                        return;
                    }
                } catch (_) {
                    /* keep polling */
                }
                await new Promise((r) => setTimeout(r, POLL_MS));
            }
            if (!cancelled) {
                setState((s) => ({
                    ...s,
                    status: "timeout",
                    message:
                        "Still processing… your purchase will activate once Stripe confirms.",
                }));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [location.search, refresh]);

    const paid = state.status === "paid";
    const gotUnlimited = state.unlimitedDaysAdded > 0;

    return (
        <div
            className="min-h-screen bg-[#0A0A0C] text-white flex items-center justify-center px-6"
            data-testid="checkout-success"
        >
            <div className="max-w-md w-full rounded-3xl border border-white/10 bg-gradient-to-br from-[#1a1a1f] to-[#0d0d10] p-8 text-center space-y-5">
                <div className="mx-auto w-14 h-14 rounded-full bg-white text-black flex items-center justify-center">
                    {paid ? (
                        gotUnlimited ? (
                            <InfinityIcon size={26} weight="bold" />
                        ) : (
                            <CheckCircleIcon size={28} weight="fill" />
                        )
                    ) : (
                        <CircleNotchIcon
                            size={28}
                            weight="bold"
                            className="animate-spin"
                        />
                    )}
                </div>
                <h1 className="font-heading text-2xl font-medium tracking-tight">
                    {paid
                        ? gotUnlimited
                            ? `Unlimited unlocked for ${state.unlimitedDaysAdded} days`
                            : `+${state.creditsAdded} photos added`
                        : state.message}
                </h1>
                {paid ? (
                    gotUnlimited ? (
                        <p className="text-sm text-white/60">
                            Active until{" "}
                            <span className="text-white font-medium">
                                {formatUntil(state.unlimitedUntil)}
                            </span>
                            .
                        </p>
                    ) : (
                        <p className="text-sm text-white/60">
                            New balance:{" "}
                            <span className="text-white font-medium">
                                {state.balance}
                            </span>{" "}
                            {state.balance === 1 ? "photo" : "photos"}.
                        </p>
                    )
                ) : (
                    <p className="text-sm text-white/60">{state.message}</p>
                )}
                <button
                    type="button"
                    onClick={() => navigate("/upload", { replace: true })}
                    data-testid="checkout-continue"
                    className="w-full inline-flex items-center justify-center rounded-full py-3 bg-white text-black font-medium hover:bg-white/90 active:scale-[0.99] transition-all"
                >
                    Continue
                </button>
            </div>
        </div>
    );
}
